import { useEffect } from "react";
import Plausible from "plausible-tracker";

// A thin wrapper around plausible-tracker. Callers import `trackEvent` and
// mount `<PlausiblePageviews />` once inside the router — nothing else in the
// app should reach into `plausible-tracker` directly.
//
// When `VITE_PLAUSIBLE_DOMAIN` is empty (dev, E2E, self-hosted preview),
// every helper becomes a no-op. This keeps call sites identical across envs
// and avoids leaking dev events to the prod analytics project.

type Tracker = ReturnType<typeof Plausible>;

let tracker: Tracker | null = null;
let initialized = false;

export function initAnalytics(): void {
  if (initialized) return;
  initialized = true;

  const domain = import.meta.env.VITE_PLAUSIBLE_DOMAIN as string | undefined;
  const apiHost = import.meta.env.VITE_PLAUSIBLE_API_HOST as string | undefined;

  if (!domain || domain.trim() === "") {
    // Leave tracker = null; trackEvent and the hook become no-ops.
    return;
  }

  tracker = Plausible({
    domain: domain.trim(),
    apiHost: apiHost && apiHost.trim() !== "" ? apiHost.trim() : undefined,
    // Don't track local dev even if someone sets a domain by mistake.
    trackLocalhost: false,
  });
}

type EventProps = Record<string, string | number | boolean>;

export function trackEvent(name: string, props?: EventProps): void {
  if (!tracker) return;
  try {
    tracker.trackEvent(name, props ? { props } : undefined);
  } catch {
    // Swallow — analytics must never break the app.
  }
}

/**
 * Component that wires `enableAutoPageviews` on mount and cleans up on
 * unmount. Mount it once inside the router tree so plausible-tracker can
 * observe `history.pushState`. Renders nothing.
 */
export function PlausiblePageviews(): null {
  useEffect(() => {
    if (!tracker) return;
    const cleanup = tracker.enableAutoPageviews();
    return cleanup;
  }, []);
  return null;
}
